import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, KeyRound, Plus, Trash2, Copy, Check, Info, Plug, Power, ShieldCheck, Terminal, CalendarClock, Save, Activity, RefreshCw, AlertTriangle, Lock } from 'lucide-react';
import AdminHeader from './AdminHeader';
import { techKeys, techCreateKey, techToggleKey, techDeleteKey, techSyncSettings, techSaveSyncSettings, techStatus, techPurge } from '../lib/marketingApi';

const PURGE_OPTIONS = [
  { key: 'app_registrations', label: 'App Registrations', desc: 'Orgs, users, plan & credit events, usage, sites' },
  { key: 'contacts', label: 'Contacts & Companies', desc: 'All CRM contacts and their aggregated companies' },
  { key: 'enquiries', label: 'Enquiries (leads)', desc: 'All contact / demo / enquiry submissions' },
  { key: 'subscribers', label: 'Newsletter subscribers', desc: 'All newsletter sign-ups' },
  { key: 'website_visits', label: 'Website visits & conversions', desc: 'Page views, unique visitors, geo cache' },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function CopyField({ value, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) { toast.error('Copy failed'); }
  };
  return (
    <div className="flex items-stretch gap-2">
      <code className="flex-1 truncate rounded-md border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-xs text-slate-700" data-testid={`${testid}-value`}>{value}</code>
      <button onClick={copy} data-testid={`${testid}-copy`} className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100">
        {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />} {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

function fmt(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch (e) { return iso; }
}

function relTime(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diff = Date.now() - then;
  const m = Math.round(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hr${h > 1 ? 's' : ''} ago`;
  const d = Math.round(h / 24);
  return `${d} day${d > 1 ? 's' : ''} ago`;
}

export default function TechConfig() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ keys: [], sync_path: '/api/marketing', ping_path: '', daily_path: '', env_key_configured: false });
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState(null); // { key, item }
  const [syncDate, setSyncDate] = useState('');
  const [savedSyncDate, setSavedSyncDate] = useState('');
  const [savingSync, setSavingSync] = useState(false);
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  // Danger zone
  const [purgeTargets, setPurgeTargets] = useState({});
  const [purgePassword, setPurgePassword] = useState('');
  const [purgeArmed, setPurgeArmed] = useState(false);
  const [purging, setPurging] = useState(false);

  const loadStatus = () => {
    setStatusLoading(true);
    techStatus().then(setStatus).catch(() => {}).finally(() => setStatusLoading(false));
  };

  const load = () => {
    setLoading(true);
    techKeys().then(setData).catch(() => toast.error('Failed to load tech config')).finally(() => setLoading(false));
    techSyncSettings().then((d) => { setSyncDate(d.sync_start_date || ''); setSavedSyncDate(d.sync_start_date || ''); }).catch(() => {});
    loadStatus();
  };
  useEffect(load, []);

  const selectedTargets = PURGE_OPTIONS.filter((o) => purgeTargets[o.key]).map((o) => o.key);
  const toggleTarget = (k) => setPurgeTargets((p) => ({ ...p, [k]: !p[k] }));
  const runPurge = async () => {
    setPurging(true);
    try {
      const d = await techPurge(purgePassword, selectedTargets);
      toast.success(`Deleted ${d.total} record${d.total === 1 ? '' : 's'} across ${selectedTargets.length} data type${selectedTargets.length === 1 ? '' : 's'}.`);
      setPurgeArmed(false); setPurgePassword(''); setPurgeTargets({});
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally { setPurging(false); }
  };


  const saveSync = async (clear = false) => {
    setSavingSync(true);
    try {
      const val = clear ? '' : syncDate;
      const d = await techSaveSyncSettings(val);
      setSyncDate(d.sync_start_date || '');
      setSavedSyncDate(d.sync_start_date || '');
      toast.success(d.sync_start_date ? `Sync start date set to ${d.sync_start_date}` : 'Sync start date cleared');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSavingSync(false); }
  };

  const syncUrl = `${BACKEND_URL}${data.sync_path || '/api/marketing'}`;

  const generate = async () => {
    setCreating(true);
    try {
      const d = await techCreateKey(newName.trim());
      setRevealed({ key: d.key, item: d.item });
      setNewName('');
      toast.success('API key generated — copy it now');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate key'); }
    finally { setCreating(false); }
  };

  const toggle = async (id) => {
    try { const d = await techToggleKey(id); toast.success(d.active ? 'Key activated' : 'Key deactivated'); load(); }
    catch (e) { toast.error('Update failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this API key permanently? The app using it will lose sync access immediately.')) return;
    try { await techDeleteKey(id); toast.success('Key deleted'); load(); }
    catch (e) { toast.error('Delete failed'); }
  };

  const inputCls = 'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#0035A8]';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="tech-config-page">
      <AdminHeader active="tech-config" />
      <div className="mx-auto max-w-5xl px-5 py-6 sm:px-8">
        <div className="mb-4 flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0035A8]/10 text-[#0035A8]"><Plug className="h-4.5 w-4.5" /></span>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">Tech Config</h1>
            <p className="text-sm text-slate-500">Connect the BillPortal app to this CRM — generate a key and load it into your app config.</p>
          </div>
        </div>

        {/* Tabs (single tab for now) */}
        <div className="mb-5 inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-100 p-1" data-testid="tech-config-tabs">
          <span className="inline-flex items-center gap-1.5 rounded-md bg-white px-3.5 py-1.5 text-sm font-semibold text-[#0035A8] shadow-sm">
            <KeyRound className="h-4 w-4" /> Sync API Keys
          </span>
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#0035A8]" /></div>
        ) : (
          <div className="space-y-4">
            {/* Connection status */}
            {(() => {
              const inbound = status?.inbound || {};
              const online = inbound.last_at && (Date.now() - new Date(inbound.last_at).getTime()) < 36 * 60 * 60 * 1000;
              const pull = status?.pull || {};
              return (
                <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="connection-status">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                      <Activity className="h-4 w-4 text-[#0035A8]" /> Connection status
                    </div>
                    <button onClick={loadStatus} data-testid="refresh-status-btn" className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100">
                      <RefreshCw className={`h-3.5 w-3.5 ${statusLoading ? 'animate-spin' : ''}`} /> Refresh
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {/* App -> CRM */}
                    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid="status-inbound">
                      <div className="mb-2 flex items-center gap-2">
                        <span className={`inline-flex h-2.5 w-2.5 rounded-full ${online ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                        <span className="text-sm font-semibold text-slate-700">App → CRM (inbound)</span>
                        <span className={`ml-auto rounded-full px-2 py-0.5 text-[11px] font-semibold ${online ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'}`} data-testid="inbound-badge">
                          {online ? 'Connected' : 'No recent activity'}
                        </span>
                      </div>
                      <dl className="space-y-1.5 text-xs text-slate-600">
                        <div className="flex justify-between gap-3"><dt className="text-slate-400">Last contact</dt><dd className="text-right font-medium" data-testid="inbound-last">{relTime(inbound.last_at) ? `${relTime(inbound.last_at)}` : '—'}<span className="block text-[11px] font-normal text-slate-400">{fmt(inbound.last_at)}</span></dd></div>
                        <div className="flex justify-between gap-3"><dt className="text-slate-400">Last data sync</dt><dd className="text-right font-medium">{inbound.last_data_at ? `${relTime(inbound.last_data_at)}` : 'None yet'}</dd></div>
                        <div className="flex justify-between gap-3"><dt className="text-slate-400">Endpoint</dt><dd className="text-right font-mono text-[11px]">{inbound.path || '—'}</dd></div>
                        <div className="flex justify-between gap-3"><dt className="text-slate-400">Authenticated with</dt><dd className="text-right font-medium" data-testid="inbound-key">
                          {inbound.key_source === 'env' ? 'Environment key' : (inbound.key_name ? `${inbound.key_name} (${inbound.key_prefix}…)` : (inbound.key_source ? 'Generated key' : '—'))}
                        </dd></div>
                      </dl>
                    </div>
                    {/* CRM -> App */}
                    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid="status-pull">
                      <div className="mb-2 flex items-center gap-2">
                        <span className={`inline-flex h-2.5 w-2.5 rounded-full ${pull.last_run_at ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                        <span className="text-sm font-semibold text-slate-700">CRM → App pull</span>
                        {pull.running && <span className="ml-auto rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-700">Running…</span>}
                      </div>
                      <dl className="space-y-1.5 text-xs text-slate-600">
                        <div className="flex justify-between gap-3"><dt className="text-slate-400">Last pull</dt><dd className="text-right font-medium">{pull.last_run_at ? relTime(pull.last_run_at) : 'None yet'}<span className="block text-[11px] font-normal text-slate-400">{fmt(pull.last_run_at)}</span></dd></div>
                        <div className="flex justify-between gap-3"><dt className="text-slate-400">Orgs synced</dt><dd className="text-right font-medium">{pull.last_stats?.orgs ?? '—'}</dd></div>
                        <div className="flex justify-between gap-3"><dt className="text-slate-400">Users synced</dt><dd className="text-right font-medium">{pull.last_stats?.users ?? '—'}</dd></div>
                      </dl>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Sync start date */}
            <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="sync-start-card">
              <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800">
                <CalendarClock className="h-4 w-4 text-[#0035A8]" /> Sync start date
              </div>
              <p className="mb-4 text-xs text-slate-500">Only orgs, App Registrations, enquiries, contacts and companies created on or after this date are synced in and shown in the CRM. Older records are hidden (not deleted) — clear the date to show everything again.</p>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <div className="flex-1">
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Start date</label>
                  <input type="date" value={syncDate} onChange={(e) => setSyncDate(e.target.value)} data-testid="sync-start-input" className={`${inputCls} sm:max-w-xs`} />
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => saveSync(false)} disabled={savingSync || !syncDate} data-testid="sync-start-save"
                    className="inline-flex items-center gap-1.5 rounded-md bg-[#0035A8] px-3.5 py-2 text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60">
                    {savingSync ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                  </button>
                  {savedSyncDate && (
                    <button onClick={() => saveSync(true)} disabled={savingSync} data-testid="sync-start-clear"
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3.5 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-60">
                      Clear
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-3 flex items-start gap-2 rounded-md bg-slate-50 p-3 text-xs text-slate-500">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{savedSyncDate ? `Active — the CRM only shows data from ${savedSyncDate} onward.` : 'No cutoff set — all synced data is shown. Set a date to start clean for go-live.'}</span>
              </div>
            </div>

            {/* Connection details */}
            <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="connection-details">
              <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800">
                <Terminal className="h-4 w-4 text-[#0035A8]" /> Connection details
              </div>
              <p className="mb-4 text-xs text-slate-500">Paste these into your BillPortal app's CRM Sync settings. The app must send the API key in the <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[11px]">X-Sync-Key</code> header on every request.</p>
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">CRM Sync base URL</label>
                  <CopyField value={syncUrl} testid="sync-url" />
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Auth header</label>
                    <CopyField value="X-Sync-Key" testid="auth-header" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Health check (GET)</label>
                    <CopyField value={`${BACKEND_URL}${data.ping_path || '/api/marketing/sync/ping'}`} testid="ping-url" />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Daily sync endpoint (POST)</label>
                  <CopyField value={`${BACKEND_URL}${data.daily_path || '/api/marketing/sync/subscriptions/daily'}`} testid="daily-url" />
                </div>
              </div>
            </div>

            {/* Generate + list */}
            <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="api-keys-card">
              <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800">
                <KeyRound className="h-4 w-4 text-[#0035A8]" /> API keys
              </div>
              <p className="mb-4 text-xs text-slate-500">Generate a key, copy it once, and load it into your app config. You can keep several keys active (e.g. one per environment) and revoke any of them.</p>

              <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end">
                <div className="flex-1">
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Key name (optional)</label>
                  <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Production app" data-testid="key-name-input" className={inputCls} />
                </div>
                <button onClick={generate} disabled={creating} data-testid="generate-key-btn"
                  className="inline-flex items-center justify-center gap-1.5 rounded-md bg-[#0035A8] px-3.5 py-2 text-sm font-semibold text-white hover:bg-[#002B87] disabled:opacity-60">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Generate key
                </button>
              </div>

              {revealed && (
                <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="revealed-key">
                  <div className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-emerald-800">
                    <ShieldCheck className="h-4 w-4" /> Copy your new key now — it won't be shown again
                  </div>
                  <CopyField value={revealed.key} testid="new-key" />
                  <button onClick={() => setRevealed(null)} data-testid="dismiss-revealed-key" className="mt-2 text-xs font-semibold text-emerald-700 hover:underline">I've copied it — hide</button>
                </div>
              )}

              {data.env_key_configured && (
                <div className="mb-4 flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 p-3 text-xs text-sky-800" data-testid="env-key-note">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>An environment key (<code className="font-mono">MARKETING_SYNC_API_KEY</code>) is also configured on the server and remains valid alongside the keys below.</span>
                </div>
              )}

              {data.keys.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 py-10 text-center text-sm text-slate-400" data-testid="keys-empty">
                  No API keys yet. Generate one to connect your app.
                </div>
              ) : (
                <div className="overflow-hidden rounded-lg border border-slate-200" data-testid="keys-list">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-2.5">Name</th>
                        <th className="px-4 py-2.5">Key</th>
                        <th className="px-4 py-2.5">Created</th>
                        <th className="px-4 py-2.5">Last used</th>
                        <th className="px-4 py-2.5">Status</th>
                        <th className="px-4 py-2.5 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.keys.map((k) => (
                        <tr key={k.id} data-testid={`key-row-${k.id}`} className="text-slate-700">
                          <td className="px-4 py-3 font-medium">{k.name}</td>
                          <td className="px-4 py-3 font-mono text-xs text-slate-500">{k.prefix}…••••</td>
                          <td className="px-4 py-3 text-xs text-slate-500">{fmt(k.created_at)}</td>
                          <td className="px-4 py-3 text-xs text-slate-500">{fmt(k.last_used_at)}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${k.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                              {k.active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1.5">
                              <button onClick={() => toggle(k.id)} data-testid={`key-toggle-${k.id}`} title={k.active ? 'Deactivate' : 'Activate'}
                                className={`inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-semibold ${k.active ? 'text-amber-600 hover:bg-amber-50' : 'text-emerald-600 hover:bg-emerald-50'}`}>
                                <Power className="h-3.5 w-3.5" /> {k.active ? 'Deactivate' : 'Activate'}
                              </button>
                              <button onClick={() => remove(k.id)} data-testid={`key-delete-${k.id}`} title="Delete" className="text-slate-300 hover:text-rose-600">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Danger zone — password-protected bulk delete */}
            <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-5" data-testid="danger-zone">
              <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-rose-700">
                <AlertTriangle className="h-4 w-4" /> Danger zone — bulk delete
              </div>
              <p className="mb-4 text-xs text-rose-600/90">Permanently delete App Registration and CRM data. This cannot be undone. Contacts are rebuilt from Enquiries &amp; Subscribers on restart — to remove them for good, also select those or set a Sync start date above.</p>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {PURGE_OPTIONS.map((o) => (
                  <label key={o.key} data-testid={`purge-opt-${o.key}`} className={`flex cursor-pointer items-start gap-2.5 rounded-lg border p-3 transition-colors ${purgeTargets[o.key] ? 'border-rose-300 bg-white' : 'border-slate-200 bg-white/70 hover:border-slate-300'}`}>
                    <input type="checkbox" checked={!!purgeTargets[o.key]} onChange={() => toggleTarget(o.key)} data-testid={`purge-check-${o.key}`} className="mt-0.5 h-4 w-4 accent-rose-600" />
                    <span>
                      <span className="block text-sm font-semibold text-slate-800">{o.label}</span>
                      <span className="block text-xs text-slate-500">{o.desc}</span>
                    </span>
                  </label>
                ))}
              </div>

              {!purgeArmed ? (
                <button
                  onClick={() => setPurgeArmed(true)}
                  disabled={selectedTargets.length === 0}
                  data-testid="purge-arm-btn"
                  className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-rose-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50">
                  <Trash2 className="h-4 w-4" /> Delete selected data
                </button>
              ) : (
                <div className="mt-4 rounded-lg border border-rose-300 bg-white p-4" data-testid="purge-confirm">
                  <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-rose-700">
                    <Lock className="h-4 w-4" /> Confirm permanent deletion
                  </div>
                  <p className="mb-3 text-xs text-slate-600">You are about to permanently delete: <strong className="text-rose-700">{selectedTargets.map((k) => PURGE_OPTIONS.find((o) => o.key === k)?.label).join(', ')}</strong>. Enter your account password to confirm.</p>
                  <input
                    type="password"
                    value={purgePassword}
                    onChange={(e) => setPurgePassword(e.target.value)}
                    placeholder="Your admin password"
                    data-testid="purge-password"
                    autoComplete="off"
                    className={`${inputCls} sm:max-w-sm`} />
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      onClick={runPurge}
                      disabled={purging || !purgePassword}
                      data-testid="purge-confirm-btn"
                      className="inline-flex items-center gap-1.5 rounded-md bg-rose-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50">
                      {purging ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Yes, permanently delete
                    </button>
                    <button
                      onClick={() => { setPurgeArmed(false); setPurgePassword(''); }}
                      disabled={purging}
                      data-testid="purge-cancel-btn"
                      className="rounded-md border border-slate-300 px-3.5 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
